import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PackingListDisplay } from '@/components/packing/PackingListDisplay';
import { BlogStructuredData } from '@/components/blog/BlogStructuredData';
import { BlogOpenGraph } from '@/components/blog/BlogOpenGraph';
import { calculateReadingTime } from '@/lib/blog-utils';
import { BlogPostHeader } from '@/components/blog/admin/BlogPostHeader';
import { BlogPostMetadata } from '@/components/blog/admin/BlogPostMetadata';
import { BlogPostContent } from '@/components/blog/admin/BlogPostContent';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', id],
    queryFn: async () => {
      const { data: blogPost, error: blogError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (blogError) throw blogError;
      if (!blogPost) throw new Error('Blog post not found');

      let authorData = null;
      if (blogPost.author_id) {
        const { data: author, error: authorError } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', blogPost.author_id)
          .maybeSingle();
        
        if (!authorError && author) {
          authorData = author;
        }
      }

      const { data: tagsData, error: tagsError } = await supabase
        .from('blog_posts_tags')
        .select('tags(*)')
        .eq('blog_post_id', id);

      const tags = tagsError ? [] : tagsData.map(t => t.tags);

      return {
        ...blogPost,
        author: authorData,
        tags
      };
    },
  });

  const readingTimeMinutes = post?.content ? calculateReadingTime(post.content) : 0;
  const baseUrl = window.location.origin;
  const currentUrl = `${baseUrl}/blog/${post?.slug || ''}`;

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Blog post deleted successfully');
      navigate('/dashboard/blogs');
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error(`Failed to delete: ${error.message}`);
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="glass-card">
        <CardContent className="flex flex-col items-center justify-center h-64">
          <p className="text-destructive text-lg mb-4">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <Button onClick={() => navigate('/dashboard/blogs')}>
            Back to Blogs
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {post && (
        <>
          <BlogStructuredData post={post} baseUrl={baseUrl} />
          <BlogOpenGraph post={post} baseUrl={baseUrl} />
        </>
      )}
      
      <BlogPostHeader 
        onEdit={() => navigate(`/dashboard/blogs/${id}/edit`)}
        onDelete={handleDelete}
        onBack={() => navigate('/dashboard/blogs')}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />

      {post && (
        <>
          <BlogPostContent
            title={post.title}
            excerpt={post.excerpt}
            content={post.content || ''}
            featuredImage={post.featured_image}
            currentUrl={currentUrl}
          />

          <BlogPostMetadata
            status={post.status || ''}
            createdAt={post.created_at || ''}
            scheduledAt={post.scheduled_at}
            author={post.author}
            viewCount={post.view_count}
            readingTimeMinutes={readingTimeMinutes}
            tags={post.tags}
          />

          <PackingListDisplay blogPostId={id!} />
        </>
      )}
    </div>
  );
};

export default BlogPost;
